import { DatePipe, formatDate, getLocaleFirstDayOfWeek } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepickerNavigateEvent, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { EventsService } from 'src/app/services/events.service';
import { CartService } from 'src/app/services/cart.service';

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EventsComponent implements OnInit {
  eventCards: any[] = [];
  fechasEvent!: any;
  dateString: string = '';
  reservationId: number | null = null;
  currentPage: number = 1;
  lastPage: number = 1;
  loadingMore: boolean = false;
  private scrollTimeout: any = null;
  loadingSpinner: boolean = false;

  // Filtros
  searchText: string = '';
  private searchSubject = new Subject<string>();

  // Date picker
  stringDate: string = '';
  openDatePicker: boolean = false;
  fromDate!: any;
  toDate!: any;
  hoveredDate!: any;
  currentMonth: number;
  minDate: NgbDateStruct;
  model: NgbDateStruct;
  @ViewChild('fechaDropdown') fechaDropdown!: ElementRef;

  constructor(
    private eventsService: EventsService,
    private router: Router,
    private datePipe: DatePipe,
    private activatedRoute: ActivatedRoute,
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
    public cartService: CartService
  ) {
    this.model = this.calendar.getToday();
    this.minDate = { day: 1, month: 0, year: 2020 };
    this.currentMonth = this.model.month;
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      const reservationParam = params['reservationId'];
      this.reservationId = reservationParam ? +reservationParam : null;
      if (!this.reservationId) {
        this.cartService.getCart().subscribe();
      }
    });

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.applyFilters();
    });
    this.getEvents();
  }

  private buildFilters(): { search?: string; date_from?: string; date_to?: string } {
    const filters: { search?: string; date_from?: string; date_to?: string } = {};
    if (this.searchText?.trim()) {
      filters.search = this.searchText.trim();
    }
    if (this.fromDate) {
      const from = this.formatStringDate(this.formatter.format(this.fromDate), 'yyyy-MM-dd');
      filters.date_from = from!;
      if (this.toDate) {
        filters.date_to = this.formatStringDate(this.formatter.format(this.toDate), 'yyyy-MM-dd')!;
      } else {
        filters.date_to = from!;
      }
    }
    return filters;
  }

  onSearchChange(value: string) {
    this.searchText = value;
    this.searchSubject.next(value);
  }

  clearFilters() {
    this.searchText = '';
    this.fromDate = null;
    this.toDate = null;
    this.stringDate = '';
    this.applyFilters();
  }

  private applyFilters() {
    this.currentPage = 1;
    this.eventCards = [];
    this.getEvents();
  }

  // Date picker methods
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    if (
      this.openDatePicker &&
      this.fechaDropdown &&
      !this.fechaDropdown.nativeElement.contains(event.target)
    ) {
      this.openDatePicker = false;
    }
  }

  changeDate(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
      this.openDatePicker = false;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    const dateString = { min: this.formatter.format(this.fromDate), max: this.formatter.format(this.toDate) };

    let stringDates = this.formatStringDate(dateString.min)!;
    if (dateString.max && dateString.max !== '') {
      stringDates += ' - ' + this.formatStringDate(dateString.max);
    }
    this.stringDate = stringDates;
    this.applyFilters();
  }

  dateNavigate($event: NgbDatepickerNavigateEvent) {
    this.currentMonth = $event.next.month;
  }

  formatStringDate(date: any, format: string = 'dd MMM yy') {
    let dateFormat = null;
    if (date) {
      dateFormat = formatDate(new Date(date), format, 'es');
    }
    return dateFormat;
  }

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }
  isMonday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getFirstDayWeek();
  isSunday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getLastDayWeek();
  isWeekend = (date: NgbDate) => this.calendar.getWeekday(date) >= 6;
  isDisabled = (date: NgbDate) => !(date.equals(this.minDate) || (date.after(this.minDate) && (date.month == this.currentMonth)));

  isFromDate(date: NgbDate) {
    return this.fromDate ? date.equals(this.fromDate) : false;
  }

  isToDate(date: NgbDate) {
    return this.toDate ? date.equals(this.toDate) : false;
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (date.equals(this.fromDate) && this.toDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date);
  }

  getFirstDayWeek() {
    return getLocaleFirstDayOfWeek('es');
  }

  getLastDayWeek() {
    let first = getLocaleFirstDayOfWeek('es');
    let lastCal = first + 6;
    if (lastCal > 7) {
      lastCal = first - (lastCal - 7);
    }
    return lastCal;
  }

  getEvents() {
    this.eventsService.getEventsActive(this.currentPage, this.buildFilters()).subscribe({
      next: (res) => {
        if (res?.success == true) {
          // Guardar información de paginación
          this.lastPage = res.data.last_page || 1;

          // Si es la primera página, reemplazar datos
          if (this.currentPage === 1) {
            this.eventCards = res.data.data;
          } else {
            // Si no es la primera página, añadir a los datos existentes
            this.eventCards = [...this.eventCards, ...res.data.data];
          }

          this.loadingMore = false;
          this.loadingSpinner = false;
        }
      }
    });
  }

  redirectToEvent(idEvent: number) {
    // Propagar reservationId si estamos en modo reserva
    const reservationId = this.activatedRoute.snapshot.queryParams['reservationId'];
    if (reservationId) {
      this.router.navigate(['event-sale/' + idEvent], { queryParams: { reservationId } });
    } else {
      this.router.navigate(['event-sale/' + idEvent]);
    }
  }

  onScroll(event: any) {
    // Limpiar el timeout anterior si existe
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    if (this.currentPage < this.lastPage) {
      this.loadingSpinner = true;
    }
    // Crear un nuevo timeout de 500ms
    this.scrollTimeout = setTimeout(() => {
      const tableWrapper = document.querySelector('.events-wrapper');
      if (tableWrapper) {
        const scrollPosition = window.pageYOffset + window.innerHeight;
        const tableBottom = tableWrapper.getBoundingClientRect().bottom + window.pageYOffset;

        // Si estamos cerca del final (100px antes del final), no estamos cargando y hay más páginas
        if (scrollPosition >= tableBottom - 100 && !this.loadingMore && this.currentPage < this.lastPage) {
          this.loadingMore = true;
          this.currentPage++;
          this.getEvents();
        }
      }
    }, 500);
  }


}
